import type { RouteRecordRaw } from 'vue-router'
import type { TreeNodeData } from '@arco-design/web-vue'
import { useAuthStore } from '@/stores/auth'
import { isAdmin } from '@/utils/app-config'

/**
 * 菜单项类型定义
 */
export interface MenuItem extends TreeNodeData {
  /** 菜单唯一标识 */
  key: string
  /** 菜单标题 */
  title: string
  /** 菜单标题英文（来自后端权限 nameEn） */
  titleEn?: string
  /** 菜单图标 */
  icon?: string
  /** 路由路径 */
  path?: string
  /** 子菜单 */
  children?: MenuItem[]
  /** 所需权限 */
  permissions?: string[]
  /** 是否隐藏 */
  hidden?: boolean
  /** 菜单排序 */
  order?: number
  /** 是否为外部链接 */
  external?: boolean
  /** 外部链接地址 */
  href?: string
  /** 菜单层级 */
  level?: number
  /** 路由元信息 */
  meta?: any
}

/**
 * 面包屑项类型定义
 */
export interface BreadcrumbItem {
  /** 标题 */
  title: string
  /** 路径（可选，最后一项通常无路径） */
  path?: string
  /** 图标 */
  icon?: string
}

/**
 * 从路由配置生成菜单
 */
export function generateMenuFromRoutes(
  routes: RouteRecordRaw[],
  parentPath = '',
  level = 1
): MenuItem[] {
  const menuItems: MenuItem[] = []

  routes.forEach(route => {
    const meta = route.meta

    // 跳过隐藏的路由和没有meta的路由
    if (meta?.hidden || !meta) {
      return
    }

    // 构建完整路径
    let fullPath: string
    if (route.path.startsWith('/')) {
      fullPath = route.path
    } else {
      if (parentPath) {
        // 确保父路径以/结尾，子路径不以/开头
        const normalizedParentPath = parentPath.endsWith('/') ? parentPath.slice(0, -1) : parentPath
        fullPath = `${normalizedParentPath}/${route.path}`
      } else {
        fullPath = `/${route.path}`
      }
    }

    const menuItem: MenuItem = {
      key: route.name as string || route.path,
      title: meta.title as string,
      titleEn: meta.titleEn as string | undefined,
      icon: meta.icon as string,
      order: meta.order as number || 999,
      level,
      meta
    }

    // 处理子路由
    if (route.children && route.children.length > 0) {
      const childMenus = generateMenuFromRoutes(route.children, fullPath, level + 1)
      if (childMenus.length > 0) {
        menuItem.children = childMenus

        // 如果父级路由有自己的组件，则设置path，这样可以直接点击跳转
        if (route.component) {
          menuItem.path = fullPath
        }
      }
    } else {
      // 叶子节点，设置path
      menuItem.path = fullPath
    }

    menuItems.push(menuItem)
  })

  // 按order排序
  return menuItems.sort((a, b) => (a.order || 999) - (b.order || 999))
}


/**
 * 根据当前路由生成面包屑
 * @param currentPath 当前路径
 * @param routes 路由配置
 * @returns 面包屑数组
 */
export function generateBreadcrumb(currentPath: string, routes: RouteRecordRaw[]): MenuItem[] {
  const breadcrumbs: MenuItem[] = []

  function findRoute(routes: RouteRecordRaw[], targetPath: string, parents: MenuItem[] = []): boolean {
    for (const route of routes) {
      const meta = route.meta
      if (!meta || meta.hidden) continue

      const fullPath = route.path.startsWith('/') ? route.path : parents.length > 0 ? `${parents[parents.length - 1].path}${route.path}` : route.path

      const currentBreadcrumb: MenuItem = {
        key: route.name as string || route.path,
        title: meta.title as string,
        titleEn: meta.titleEn as string | undefined,
        icon: meta.icon as string,
        path: fullPath
      }

      const newParents = [...parents, currentBreadcrumb]

      if (fullPath === targetPath) {
        breadcrumbs.push(...newParents)
        return true
      }

      if (route.children) {
        if (findRoute(route.children, targetPath, newParents)) {
          return true
        }
      }
    }
    return false
  }

  findRoute(routes, currentPath)
  return breadcrumbs
}

/**
 * 根据路径查找菜单项
 */
export function findMenuByPath(menu: MenuItem[], path: string): MenuItem | null {
  for (const item of menu) {
    if (item.path === path) {
      return item
    }
    if (item.children) {
      const found = findMenuByPath(item.children, path)
      if (found) {
        return found
      }
    }
  }
  return null
}

/**
 * 获取菜单的打开keys（用于展开菜单）
 */
export function getOpenMenuKeys(menu: MenuItem[], currentPath: string): string[] {
  const openKeys: string[] = []

  function findPath(items: MenuItem[], targetPath: string, parentKeys: string[] = []): boolean {
    for (const item of items) {
      const currentKeys = [...parentKeys, item.key]

      if (item.path === targetPath) {
        openKeys.push(...parentKeys)
        return true
      }

      if (item.children && item.children.length > 0) {
        if (findPath(item.children, targetPath, currentKeys)) {
          return true
        }
      }
    }
    return false
  }

  findPath(menu, currentPath)
  return openKeys
}

/**
 * 获取当前选中的菜单key
 */
export function getSelectedMenuKey(menu: MenuItem[], currentPath: string): string[] {
  const selectedKeys: string[] = []

  function findSelected(items: MenuItem[]): boolean {
    for (const item of items) {
      if (item.path === currentPath) {
        selectedKeys.push(item.key)
        return true
      }

      if (item.children && item.children.length > 0) {
        if (findSelected(item.children)) {
          return true
        }
      }
    }
    return false
  }

  findSelected(menu)
  return selectedKeys
}

/**
 * 扁平化菜单（获取所有有路径的菜单项）
 */
export function flattenMenu(menu: MenuItem[]): MenuItem[] {
  const result: MenuItem[] = []

  function traverse(items: MenuItem[]) {
    items.forEach(item => {
      if (item.path) {
        result.push(item)
      }
      if (item.children) {
        traverse(item.children)
      }
    })
  }

  traverse(menu)
  return result
}

/**
 * 根据权限过滤菜单
 */
export function filterMenuByPermissions(menuItems: MenuItem[], rolePermissionCodes: string[]): MenuItem[] {
  const authStore = useAuthStore()
  return menuItems
    .filter(item => {
      // 管理员角色展示所有菜单
      if (authStore.adminRole) return true

      // 没有 permission 字段，默认展示
      if (!item.meta?.permission) return true

      // rolePermissionCodes 包含 meta.permission 才展示
      return rolePermissionCodes.includes(item.meta.permission)
    })
    .map(item => {
      if (item.children) {
        return {
          ...item,
          children: filterMenuByPermissions(item.children, rolePermissionCodes)
        }
      }
      return item
    })
}

/** 首页模块权限码（CCTOWER-2867） */
export const HOME_PERMISSION = 'module:home:home'

/**
 * 当前用户是否拥有首页权限。
 * 首页路由不带 meta.permission（不参与 filterRoutesByPermission 过滤），
 * 这里基于菜单权限（userPermissions）单独判断。
 * 运营端（admin 应用）首页不受租户端权限控制，直接放行（PRD 仅适用于租户端 Client）。
 * 客户端内 roleType=1 的企业管理员同样属于 Client 用户（PRD §3.1），首页权限统一受 module:home:home 控制，
 * 不因 adminRole 放行。
 */
export function hasHomePermission(): boolean {
  if (isAdmin()) {
    return true
  }
  const authStore = useAuthStore()
  return (authStore.userPermissions || []).some(
    p => p.permissionCode === HOME_PERMISSION && p.status === 1
  )
}

/**
 * 递归移除"首页"菜单项（无首页权限时隐藏侧边栏首页入口）。
 */
export function removeHomeMenu(menuItems: MenuItem[]): MenuItem[] {
  return menuItems
    .filter(item => item.key !== 'Home' && item.path !== '/home')
    .map(item => {
      if (item.children?.length) {
        return {
          ...item,
          children: removeHomeMenu(item.children)
        }
      }
      return item
    })
}

/** legacy 租户隐藏统一下单 v2 专属菜单（由配置中心控制，非 RBAC） */
export function filterUnifiedOrderV2Menus(menuItems: MenuItem[], unifiedOrderV2Enabled: boolean): MenuItem[] {
  if (unifiedOrderV2Enabled) {
    return menuItems
  }
  return menuItems.reduce<MenuItem[]>((acc, item) => {
    if (item.meta?.requiresUnifiedOrderV2) {
      return acc
    }
    if (item.children?.length) {
      const children = filterUnifiedOrderV2Menus(item.children, unifiedOrderV2Enabled)
      if (children.length === 0 && item.meta?.isMenuGroup) {
        return acc
      }
      acc.push({ ...item, children })
      return acc
    }
    acc.push(item)
    return acc
  }, [])
}

/** 客户端隐藏指定菜单（如委托下单），仅从菜单树移除，不影响路由注册与页面跳转 */
export function filterClientHiddenMenus(menuItems: MenuItem[], isClient: boolean): MenuItem[] {
  if (!isClient) {
    return menuItems
  }
  return menuItems.reduce<MenuItem[]>((acc, item) => {
    if (item.meta?.hideForClient) {
      return acc
    }
    if (item.children?.length) {
      const children = filterClientHiddenMenus(item.children, isClient)
      if (children.length === 0 && item.meta?.isMenuGroup) {
        return acc
      }
      acc.push({ ...item, children })
      return acc
    }
    acc.push(item)
    return acc
  }, [])
}

/**
 * 获取默认展开的菜单keys
 * @param menuItems 菜单项
 * @param currentPath 当前路径
 * @returns 需要展开的菜单key数组
 */
export function getDefaultOpenKeys(menuItems: MenuItem[], currentPath: string): string[] {
  const openKeys: string[] = []

  function findOpenKeys(items: MenuItem[], path: string): boolean {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        // 检查子菜单中是否包含当前路径
        const hasActivePath = findOpenKeys(item.children, path)
        if (hasActivePath) {
          openKeys.push(item.key)
          return true
        }
      } else if (item.path === path) {
        return true
      }
    }
    return false
  }

  findOpenKeys(menuItems, currentPath)
  return openKeys
}

/**
 * 获取当前激活的菜单key
 * @param menuItems 菜单项
 * @param currentPath 当前路径
 * @returns 激活的菜单key
 */
export function getActiveMenuKey(menuItems: MenuItem[], currentPath: string): string {
  function findActiveKey(items: MenuItem[]): string {
    for (const item of items) {
      if (item.path === currentPath) {
        return item.key
      }
      if (item.children) {
        const childKey = findActiveKey(item.children)
        if (childKey) {
          return childKey
        }
      }
    }
    return ''
  }

  return findActiveKey(menuItems)
}
